import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import * as Inovx from '@shared';
import HomeStore from '@admin/home/stores/HomeStore';
import HomeActions from '@admin/home/actions/HomeActions';

const PermissionService = Inovx.Shared.Permission.PermissionService;

// Admin submenu items — path must match the <Route> in main.tsx
const ADMIN_ITEMS = [
    { name: 'User', path: '/user', labelKey: 'home:menu.registeredUsers' },
    { name: 'Entitlement', path: '/entitlement', labelKey: 'home:menu.entitlement' },
    { name: 'Role', path: '/role', labelKey: 'home:menu.organizationRoles' },
    { name: 'Organization', path: '/organization', labelKey: 'home:menu.accessRights' },
    { name: 'License', path: '/license', labelKey: 'home:menu.license' },
    { name: 'Diagnostics', path: '/diagnostics', labelKey: 'home:menu.systemLogs' },
];

// Check whether an admin app should appear in the sidebar.
// Once window.Applications is populated by an API call, this will gate visibility properly.
// Until then, show all items so migrated apps are reachable.
function hasApplication(name: string): boolean {
    const applications = (window as any).Applications as string[] | undefined;
    if (!Array.isArray(applications) || applications.length === 0) {
        // Applications list not loaded yet — show all items
        return true;
    }
    return applications.includes(name);
}

function hasManageFacilityPermission(): boolean {
    return PermissionService.checkPermission('Facility.Manage');
}

const AdminSidebar: React.FC = () => {
    const location = useLocation();
    const history = useHistory();

    const [sidebarOpen, setSidebarOpen] = useState(HomeStore.getSidebarOpen());
    const [adminExpanded, setAdminExpanded] = useState(false);

    // Sync local state with HomeStore
    const onStoreChange = useCallback(() => {
        setSidebarOpen(HomeStore.getSidebarOpen());
    }, []);

    useEffect(() => {
        HomeStore.addChangeListener(onStoreChange);
        return () => HomeStore.removeChangeListener(onStoreChange);
    }, [onStoreChange]);

    // Auto-expand Admin submenu when on an admin route
    useEffect(() => {
        const isAdminRoute = ADMIN_ITEMS.some(item =>
            location.pathname.startsWith(item.path)
        );
        if (isAdminRoute) {
            setAdminExpanded(true);
            if (!sidebarOpen) HomeActions.openSidebar();
        }
    }, [location.pathname]);

    const toggleSidebar = () => {
        if (sidebarOpen) {
            HomeActions.closeSidebar();
        } else {
            HomeActions.openSidebar();
        }
    };

    const toggleAdmin = () => {
        if (sidebarOpen) setAdminExpanded(prev => !prev);
    };

    const isActive = (path: string) =>
        location.pathname === path || location.pathname.startsWith(path + '/') ? 'active' : '';

    const isAdminActive = () =>
        ADMIN_ITEMS.some(item => location.pathname.startsWith(item.path))
            ? 'active'
            : '';

    return (
        <div id="sidebar" className={`sidebar${sidebarOpen ? ' show' : ''}`}>
            <nav className="nav">
                <ul className="list-unstyled components">

                    {/* Toggle button */}
                    <li>
                        <a className="d-flex j-csb" onClick={toggleSidebar} style={{ cursor: 'pointer' }}>
                            <div className="icon">
                                <img src="/images/menus/menu_icon.png" alt="" />
                            </div>
                        </a>
                    </li>

                    {/* Home */}
                    <li className={isActive('/home')}>
                        <a className="menu-item d-flex j-csb v-tooltip"
                            href="/home"
                            aria-label={InovxLoader.t('home:menu.home')}>
                            <div className="d-flex a-ic">
                                <div className="icon">
                                    <img src="/images/menus/home_icon.png" alt="" />
                                </div>
                                <div className="nav_name">{InovxLoader.t('home:menu.home')}</div>
                            </div>
                        </a>
                    </li>

                    {/* Manage Facilities (permission-gated) */}
                    {hasManageFacilityPermission() && (
                        <li className={isActive('/manage')}>
                            <a className="menu-item d-flex j-csb v-tooltip"
                                href="/manage"
                                aria-label={InovxLoader.t('home:menu.manageFacilities')}>
                                <div className="d-flex a-ic">
                                    <div className="icon">
                                        <img src="/images/menus/manage_facilities.png" alt="" />
                                    </div>
                                    <div className="nav_name">{InovxLoader.t('home:menu.manageFacilities')}</div>
                                </div>
                            </a>
                        </li>
                    )}

                    {/* Admin (expandable submenu) */}
                    <li className={`${sidebarOpen ? '' : 'hasSubmenu'} ${isAdminActive()}`}>
                        <a className="menu-item expandable d-flex j-csb"
                            onClick={toggleAdmin}
                            style={{ cursor: 'pointer' }}>
                            <div className="d-flex a-ic">
                                <div className="icon">
                                    <img src="/images/menus/setting_icon.png" alt="" />
                                </div>
                                <div className="nav_name">{InovxLoader.t('home:menu.admin')}</div>
                            </div>
                            <div className="icon down">
                                {sidebarOpen && (
                                    <i className={`fa fa-angle-${adminExpanded ? 'down' : 'up'}`}
                                        aria-hidden="true" />
                                )}
                            </div>
                        </a>
                        <div className={`subMenu${adminExpanded ? ' show-submenu' : ''}`}>
                            {ADMIN_ITEMS.map(item =>
                                hasApplication(item.name) ? (
                                    <a key={item.name}
                                        className={isActive(item.path)}
                                        href={item.path}>{InovxLoader.t(item.labelKey)}</a>
                                ) : null
                            )}
                        </div>
                    </li>

                </ul>
            </nav>
        </div>
    );
};

export default AdminSidebar;
